/**
 * Identity-only wizard gate (join invites).
 *
 * A join-invited user lands with a clinic membership already materialised at
 * invite accept (claro-elixir `Claro.Invites.accept/1` join target), so the
 * standard `clinics.length === 0` first-run gate skips the wizard entirely —
 * and they'd never enter their practitioner credentials. This helper is the
 * one place that decides: "signed in, has a clinic, is a practitioner-role
 * member, but has no practitioner identity yet" → route through JUST the
 * PractitionerIdentity step (never FirstClinic — their clinic exists).
 *
 * ## Why this reads TWO fields (CLA-1141)
 *
 * `practitioner_type` used to answer two different questions at once. The
 * wizard, the public signup form and the owner invite all wrote an AHPRA
 * *registration status* (`registered` / `non_registered` / `admin_only`) into
 * the column the note prompt reads as a *profession*. The backend split them:
 * the wizard now writes `registration_status`, and `practitioner_type` is
 * profession-only (`doctor`, `dermal_therapist`, …).
 *
 * Either field alone is the wrong gate, in opposite directions:
 *
 *   * `registration_status` alone sends every EXISTING user who only ever had
 *     a profession recorded (8 of 28 on staging carry `doctor`, and the split
 *     migration does not invent a registration answer for them) back through
 *     a wizard they completed long ago.
 *   * `practitioner_type` alone sends every user who completes the wizard
 *     AFTER the split back through it forever — the wizard sets
 *     `registration_status`, and profession is optional there, so
 *     `practitioner_type` stays null for anyone who skips it.
 *
 * Answering either question means the identity step has been through. The
 * question this gate asks is "has this person ever been asked?", not "is
 * their profile complete" — profile completeness is the nag's job, not the
 * wizard's.
 */

import type { User, Clinic } from '@/api/types'

export function needsPractitionerIdentity(
  user: Pick<User, 'practitioner_type' | 'registration_status'> | null,
  clinics: Pick<Clinic, 'clinic_role'>[],
): boolean {
  if (!user) return false
  if (user.registration_status) return false
  if (user.practitioner_type) return false
  if (clinics.length === 0) return false // first-run wizard's job, not ours
  return clinics.some((c) => c.clinic_role === 'practitioner')
}
